import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, BookOpen, Scale, ListTree, Filter, IndianRupee, TrendingUp, TrendingDown, Landmark, Receipt } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const TABS = [
  { id: 'chart',   label: 'Chart of Accounts', icon: BookOpen },
  { id: 'trial',   label: 'Trial Balance',     icon: Scale },
  { id: 'ledger',  label: 'Ledger Explorer',   icon: ListTree },
];

const TYPE_META = {
  income:    { color: '#2E7D32', bg: '#E8F5E9', label: 'Income',    icon: TrendingUp },
  expense:   { color: '#C2410C', bg: '#FEF3C7', label: 'Expense',   icon: TrendingDown },
  liability: { color: '#7B1FA2', bg: '#F3E5F5', label: 'Liability', icon: Receipt },
  asset:     { color: '#1E88E5', bg: '#DBEAFE', label: 'Asset',     icon: Landmark },
  equity:    { color: '#6B7280', bg: '#F3F4F6', label: 'Equity',    icon: Scale },
};

const AdminAccounts = () => {
  const [tab, setTab] = useState('chart');
  const [chart, setChart] = useState(null);
  const [trial, setTrial] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [franchiseFilter, setFranchiseFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, t, fr] = await Promise.all([
        axios.get(`${API}/admin/accounts/chart${franchiseFilter ? `?franchise_id=${franchiseFilter}` : ''}`, authCfg()),
        axios.get(`${API}/admin/accounts/trial-balance${franchiseFilter ? `?franchise_id=${franchiseFilter}` : ''}`, authCfg()),
        axios.get(`${API}/franchises`, authCfg()),
      ]);
      setChart(c.data);
      setTrial(t.data);
      setFranchises(fr.data || []);
    } catch (e) { console.warn(e); }
    setLoading(false);
  };

  const loadLedger = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (accountFilter) params.set('account', accountFilter);
      if (franchiseFilter) params.set('franchise_id', franchiseFilter);
      params.set('limit', '300');
      const { data } = await axios.get(`${API}/admin/erp/ledger?${params.toString()}`, authCfg());
      setLedger(data.entries || []);
    } catch (e) { console.warn(e); }
    setBusy(false);
  };

  useEffect(() => { load(); }, [franchiseFilter]);
  useEffect(() => { if (tab === 'ledger') loadLedger(); /* eslint-disable-next-line */ }, [tab, accountFilter, franchiseFilter]);

  const groups = useMemo(() => {
    const g = { income: [], expense: [], liability: [], asset: [] };
    (chart?.accounts || []).forEach((r) => { g[r.type]?.push(r); });
    return g;
  }, [chart]);

  return (
    <AdminLayout title="Master Accounts (HQ)">
      {loading || !chart ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="accounts-filter-bar">
            <span className="text-xs font-semibold text-neutral-500 inline-flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Scope:</span>
            <select
              data-testid="accounts-franchise-filter"
              value={franchiseFilter}
              onChange={(e) => setFranchiseFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white"
            >
              <option value="">Consolidated (all franchises)</option>
              {franchises.map((f) => <option key={f.franchise_id} value={f.franchise_id}>{f.district} ({f.franchise_id})</option>)}
            </select>
            <span className="text-[11px] text-neutral-500 ml-auto">As of {new Date(chart.as_of).toLocaleString('en-IN')}</span>
          </div>

          {/* Totals hero cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {Object.entries(TYPE_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <div key={k} data-testid={`accounts-total-${k}`} className="bg-white border border-neutral-200 rounded-xl p-4">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: m.bg }}>
                    <Icon className="w-5 h-5" style={{ color: m.color }} />
                  </div>
                  <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{m.label}</p>
                  <p className="font-extrabold text-lg mt-0.5">{fmt(chart.totals?.[k] || 0)}</p>
                </div>
              );
            })}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`accounts-tab-${id}`}
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${tab === id ? 'bg-[#2E7D32] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#2E7D32]'}`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {/* Chart of Accounts */}
          {tab === 'chart' && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="accounts-chart-panel">
              {Object.entries(TYPE_META).map(([type, m]) => (
                <div key={type} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2" style={{ background: m.bg }}>
                    <m.icon className="w-4 h-4" style={{ color: m.color }} />
                    <p className="font-bold text-sm" style={{ color: m.color }}>{m.label}</p>
                    <span className="ml-auto text-xs font-extrabold" style={{ color: m.color }}>{fmt(chart.totals?.[type] || 0)}</span>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {(groups[type] || []).map((r) => (
                        <tr key={r.account} data-testid={`account-row-${r.account.replace(/[^a-z0-9]/gi, '-')}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                          <td className="px-4 py-2.5">
                            <p className="text-xs font-semibold text-neutral-800">{r.label}</p>
                            <p className="text-[10px] text-neutral-400 font-mono">{r.account}</p>
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs font-bold" style={{ color: m.color }}>{fmt(r.balance)}</td>
                          <td className="px-4 py-2.5 text-right w-16">
                            <button
                              onClick={() => { setAccountFilter(r.account.startsWith('bank:') ? '' : r.account); setTab('ledger'); }}
                              className="text-[11px] font-semibold text-[#2E7D32] hover:underline"
                              data-testid={`view-ledger-${r.account.replace(/[^a-z0-9]/gi, '-')}`}
                            >View</button>
                          </td>
                        </tr>
                      ))}
                      {(groups[type] || []).length === 0 && <tr><td colSpan={3} className="px-4 py-4 text-center text-xs text-neutral-400">No accounts.</td></tr>}
                    </tbody>
                  </table>
                </div>
              ))}
              <div className="md:col-span-2 bg-white border border-neutral-200 rounded-2xl p-4 flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-[#2E7D32]" />
                <p className="text-sm"><span className="font-bold">Net worth</span> (Income + Assets − Expense − Liability): <span className="font-extrabold text-[#2E7D32]">{fmt(chart.net_worth)}</span></p>
              </div>
            </div>
          )}

          {/* Trial Balance */}
          {tab === 'trial' && trial && (
            <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="accounts-trial-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                    <tr>
                      <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Account</th>
                      <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase text-right">Debit</th>
                      <th className="px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase text-right">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trial.rows.map((r, i) => (
                      <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-4 py-2">
                          <p className="text-xs font-semibold">{r.label}</p>
                          <p className="text-[10px] text-neutral-400 font-mono">{r.account}</p>
                        </td>
                        <td className="px-4 py-2 text-xs uppercase">{r.type}</td>
                        <td className="px-4 py-2 text-right text-xs font-bold text-[#C2410C]">{r.debit ? fmt(r.debit) : '—'}</td>
                        <td className="px-4 py-2 text-right text-xs font-bold text-[#2E7D32]">{r.credit ? fmt(r.credit) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-neutral-300 bg-neutral-50">
                    <tr>
                      <td className="px-4 py-3 text-xs font-extrabold">Totals</td>
                      <td className="px-4 py-3 text-xs" data-testid="trial-balanced-status">
                        {trial.totals.balanced
                          ? <span className="text-[#2E7D32] font-bold">Balanced ✓</span>
                          : <span className="text-red-600 font-bold">Unbalanced</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-extrabold text-[#C2410C]" data-testid="trial-total-debit">{fmt(trial.totals.debit)}</td>
                      <td className="px-4 py-3 text-right text-sm font-extrabold text-[#2E7D32]" data-testid="trial-total-credit">{fmt(trial.totals.credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Ledger Explorer */}
          {tab === 'ledger' && (
            <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="accounts-ledger-panel">
              <div className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-2">
                <p className="font-bold text-sm flex-1">Ledger — most recent 300 entries</p>
                <select
                  data-testid="ledger-account-filter"
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs bg-white"
                >
                  <option value="">All accounts</option>
                  {(chart.accounts || []).filter((a) => !a.account.startsWith('bank:')).map((a) => <option key={a.account} value={a.account}>{a.label}</option>)}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                    <tr>
                      <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase">Account</th>
                      <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase">Franchise</th>
                      <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase">Ref</th>
                      <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase">Note</th>
                      <th className="px-3 py-2 text-[10px] font-bold text-neutral-500 uppercase text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {busy && <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-neutral-400" /></td></tr>}
                    {!busy && ledger.map((e, i) => (
                      <tr key={e.id || i} data-testid={`ledger-row-${i}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-3 py-1.5 text-[11px] text-neutral-600">{(e.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-3 py-1.5 text-[11px] font-semibold">{e.account}</td>
                        <td className="px-3 py-1.5 text-[11px] text-neutral-600">{e.district || e.franchise_id || '—'}</td>
                        <td className="px-3 py-1.5 text-[10px] font-mono text-neutral-500">{e.ref_type}:{(e.ref_id || '').slice(0, 8)}</td>
                        <td className="px-3 py-1.5 text-[11px] text-neutral-500 max-w-xs truncate">{e.note || e.category || '—'}</td>
                        <td className={`px-3 py-1.5 text-right text-xs font-bold ${e.amount < 0 ? 'text-red-600' : 'text-[#2E7D32]'}`}>
                          {e.amount < 0 ? '−' : ''}₹{Math.abs(Number(e.amount) || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))}
                    {!busy && ledger.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-neutral-400">No entries.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminAccounts;
